import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ProblemBadge } from "@/components/ProblemBadge";
import { api } from "@/lib/api";
import {
  ERROR_KIND_LABEL,
  isOpenProblem,
  PROBLEM_BOX_STYLE,
  PROBLEM_TEXT,
  PROBLEM_TITLE,
} from "@/lib/callProblem";
import { CLOSURE_BANNER, CLOSURE_LABEL, inferClosureKind, type ClosureKind } from "@/lib/closure";
import {
  type CallDetail as CallDetailT,
  type CallEventRow,
  type CallProblem,
  type CallStatus,
  type IdentificationPath,
  type TokenUsageRow,
  type Utterance,
} from "@/lib/types";

/** Emmy doctor-app request URL pattern. Verified 2026-04-25 against live Emmy: requests live at
 *  `dr.sestraemmy.cz/requests/{uuid}`. Override via VITE_EMMY_TICKET_URL_PATTERN if the path ever moves. */
const EMMY_TICKET_URL_PATTERN: string =
  import.meta.env.VITE_EMMY_TICKET_URL_PATTERN ??
  "https://dr.sestraemmy.cz/requests/{ticketId}";

const buildEmmyTicketUrl = (ticketId: string): string =>
  EMMY_TICKET_URL_PATTERN.replace("{ticketId}", encodeURIComponent(ticketId));

export const CallDetail = () => {
  const { id = "" } = useParams();
  const query = useQuery({
    queryKey: ["call", id],
    queryFn: () => api.getCall(id),
    enabled: id.length > 0,
  });

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="mb-6">
        <Link to="/calls" className="text-sm text-gray-500 hover:text-[#1a2b6b]">
          ← Hovory
        </Link>
      </div>

      {query.isPending && <p className="text-gray-500">Načítám…</p>}
      {query.isError && <p className="text-red-600">Chyba: {(query.error as Error).message}</p>}
      {query.isSuccess && <CallDetailBody call={query.data} />}
    </div>
  );
};

const CallDetailBody = ({ call }: { call: CallDetailT }) => {
  // Track the live audio element via callback ref so effects can re-attach listeners when it mounts.
  // (A `useRef` doesn't trigger re-renders, so the playback-position tracking effect couldn't bind.)
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  // Auto-load the presigned URL on mount so click-to-seek works without a separate "load" gesture.
  const audioQuery = useQuery({
    queryKey: ["call-audio", call.id],
    queryFn: () => api.getCallAudioUrl(call.id),
    enabled: Boolean(call.audioS3Uri),
    staleTime: 10 * 60 * 1000, // presigned URLs are 15-min by default — refetch a touch sooner
  });

  // Pool kind drives diagnostic visibility — doctors don't see token usage / event timeline / call UUID
  // at all; admins get the same data wrapped in a click-to-expand accordion. Cached in react-query so
  // this doesn't re-fetch when navigating between calls.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000 });
  const isAdmin = me.data?.kind === "admin";
  const isDoctor = me.data?.kind === "doctor";

  const seekTo = (sec: number) => {
    if (!audioEl) return;
    audioEl.currentTime = Math.max(0, sec);
    void audioEl.play().catch(() => {
      // Autoplay can be blocked when the page hasn't been interacted with — that's fine, the click on
      // a transcript line counts as interaction so subsequent calls should succeed.
    });
  };

  const merged = useMergedTranscript(call.transcriptJson);
  const activeIndex = useActiveTranscriptLine(audioEl, merged);

  // PRD-006 schedule short-circuits: agent played a closure prompt and hung up. Klasifikace / Shrnutí /
  // Přepis are all meaningless because the post-call pipeline doesn't run for these. Collapse the page
  // into a banner + recording playback (so staff can QA the closure prompt heard by the caller).
  // Infer kind from `closeReason` (forward-compat) or `failure_reason` ("closed:vacation" prefix that
  // the agent currently emits) so the row renders as a closure regardless of which path produced it.
  const closureKind = inferClosureKind(call);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          {/* Internal call UUID is diagnostic — admins see it; doctors don't need it. */}
          {!isDoctor && <p className="mb-1 font-mono text-xs text-gray-500">{call.id}</p>}
          <h2 className="text-2xl tracking-tight text-[#1a2b6b]">
            Hovor — {call.practiceId}
          </h2>
        </div>
        {closureKind ? (
          <ClosureBadge kind={closureKind} />
        ) : isOpenProblem(call.problem) ? (
          <ProblemBadge problem={call.problem} />
        ) : (
          <StatusBadge status={call.status} />
        )}
      </header>

      {/* Real failures only — closure rows have failure_reason="closed:..." which we render as a closure
          banner instead. Without this exclusion, vacation/out-of-hours calls would show both a red error
          banner AND the friendly closure section. */}
      {call.failureReason && !closureKind && (
        <Section title="Chyba">
          <p className="text-sm text-red-700">{call.failureReason}</p>
        </Section>
      )}

      {closureKind && (
        <Section title={CLOSURE_LABEL[closureKind]}>
          <p className="text-sm text-gray-700">{CLOSURE_BANNER[closureKind]}</p>
        </Section>
      )}

      {isAdmin && call.problem && <ProblemPanel callId={call.id} problem={call.problem} />}

      <Section title="Metadata">
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          <dt className="text-gray-500">Zahájen</dt>
          <dd>{formatDate(call.startedAt)}</dd>
          <dt className="text-gray-500">Ukončen</dt>
          <dd>{call.endedAt ? formatDate(call.endedAt) : "—"}</dd>
          <dt className="text-gray-500">Telefon volajícího</dt>
          <dd className="font-mono">{call.callerPhone ?? "—"}</dd>
          <dt className="text-gray-500">Přiřazený pacient</dt>
          <dd className="font-mono">{call.matchedPatientId ?? "—"}</dd>
          <dt className="text-gray-500">Způsob identifikace</dt>
          <dd>{IDENT_LABEL[call.identificationPath] ?? call.identificationPath}</dd>
        </dl>
      </Section>

      <Section title="Nahrávka">
        <AudioPanel
          setAudioEl={setAudioEl}
          url={audioQuery.data?.url ?? null}
          isPending={audioQuery.isPending && Boolean(call.audioS3Uri)}
          isError={audioQuery.isError}
          errorMessage={(audioQuery.error as Error | null)?.message}
          hasAudio={Boolean(call.audioS3Uri)}
        />
      </Section>

      {/* Transcript / Klasifikace / Shrnutí / Interní poznámka are suppressed for closure calls — the
          post-call pipeline doesn't run, so they'd all be empty placeholders. The recording (above) is
          kept so staff can verify the closure prompt the caller actually heard. */}
      {!closureKind && (
        <>
          <Section title="Přepis">
            {merged.length > 0 ? (
              <TranscriptList lines={merged} activeIndex={activeIndex} onSeek={seekTo} />
            ) : call.transcriptText ? (
              // Fallback for legacy calls (pre-V011) — caller-only transcript without timestamps.
              <pre className="max-h-[24rem] overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-4 font-sans text-sm text-gray-800">
                {call.transcriptText}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">Zatím nepřepsáno.</p>
            )}
            {/* Raw STT JSON is admin-only — diagnostic, includes Chirp word offsets we use to triage bucketing. */}
            {isAdmin && <TranscriptDownload callId={call.id} />}
          </Section>

          <Section title="Klasifikace">
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-gray-500">Typ požadavku</dt>
              <dd>{call.requestTypeName ?? call.requestTypeId ?? "—"}</dd>
              <dt className="text-gray-500">Extrahované jméno</dt>
              <dd>{call.extractedNameClaim ?? "—"}</dd>
              <dt className="text-gray-500">Emmy požadavek</dt>
              <dd>
                {call.emmyRequestId ? (
                  <a
                    href={buildEmmyTicketUrl(call.emmyRequestId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1a2b6b] underline hover:opacity-80"
                    title="Otevřít požadavek v Emmy"
                  >
                    {call.requestTypeName ?? call.requestTypeId ?? "Otevřít požadavek"} ↗
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </dl>
          </Section>

          <Section title="Shrnutí (130 znaků)">
            <p className="text-sm">{call.summary130Char ?? "—"}</p>
          </Section>

          <Section title="Interní poznámka">
            {call.internalNote ? (
              <pre className="whitespace-pre-wrap rounded bg-gray-50 p-4 font-sans text-sm text-gray-800">
                {call.internalNote}
              </pre>
            ) : (
              <p className="text-sm text-gray-500">—</p>
            )}
          </Section>
        </>
      )}

      {/* Diagnostic data (token usage + raw event timeline) is admin-only and folded behind an accordion
          so it doesn't dominate the page. Doctors don't see it at all — they don't need cost telemetry
          or raw event JSON for their day-to-day. */}
      {isAdmin && (
        <details className="group rounded border border-gray-200 bg-white">
          <summary className="cursor-pointer list-none px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-[#1a2b6b]">
            Diagnostika
            <span className="ml-2 text-[10px] font-normal normal-case text-gray-400 group-open:hidden">
              kliknutím rozbalíte
            </span>
          </summary>
          <div className="space-y-6 border-t border-gray-200 p-6">
            <div>
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Spotřeba tokenů
              </h3>
              <TokenUsageTable rows={call.tokenUsage} />
            </div>
            <div>
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Časová osa
              </h3>
              <EventsList events={call.events} />
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="rounded border border-gray-200 bg-white p-6">
    <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
    {children}
  </section>
);

const AudioPanel = ({
  setAudioEl,
  url,
  isPending,
  isError,
  errorMessage,
  hasAudio,
}: {
  setAudioEl: (el: HTMLAudioElement | null) => void;
  url: string | null;
  isPending: boolean;
  isError: boolean;
  errorMessage?: string;
  hasAudio: boolean;
}) => {
  if (!hasAudio) return <p className="text-sm text-gray-500">Žádná nahrávka.</p>;
  if (isPending) return <p className="text-sm text-gray-500">Načítám nahrávku…</p>;
  if (isError) return <p className="text-sm text-red-600">{errorMessage ?? "Chyba při načítání nahrávky."}</p>;
  if (!url) return <p className="text-sm text-gray-500">Nahrávka není k dispozici.</p>;
  return (
    <div className="space-y-2">
      <audio ref={setAudioEl} controls src={url} preload="auto" className="w-full" />
      <a href={url} download className="text-xs text-[#1a2b6b] hover:underline">
        Stáhnout nahrávku
      </a>
    </div>
  );
};

interface MergedLine {
  speaker: "agent" | "caller";
  startSec: number;
  endSec: number;
  text: string;
}

/** Stable-sort agent + caller utterances into a single chronological stream. Stable so that within the same
 *  start-second, agent vs caller order matches insertion order, which keeps natural turn-taking visible. */
const useMergedTranscript = (
  transcript: { agent: Utterance[]; caller: Utterance[] } | null | undefined,
): MergedLine[] =>
  useMemo(() => {
    if (!transcript) return [];
    const agentLines: MergedLine[] = (transcript.agent ?? []).map((u) => ({
      speaker: "agent",
      startSec: u.startSec,
      endSec: u.endSec,
      text: u.text,
    }));
    const callerLines: MergedLine[] = (transcript.caller ?? []).map((u) => ({
      speaker: "caller",
      startSec: u.startSec,
      endSec: u.endSec,
      text: u.text,
    }));
    return [...agentLines, ...callerLines].sort((a, b) => {
      if (a.startSec !== b.startSec) return a.startSec - b.startSec;
      return a.endSec - b.endSec;
    });
  }, [transcript]);

/** Track which transcript line is currently playing. Returns the index of the latest line whose `startSec` is
 *  ≤ audio.currentTime, or -1 when nothing has started playing yet (or audio isn't mounted). The value sticks on
 *  the previous line during between-utterance gaps and pause states — that's intentional, so the highlight
 *  doesn't flicker off and on between turns. */
const useActiveTranscriptLine = (
  audioEl: HTMLAudioElement | null,
  lines: MergedLine[],
): number => {
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (!audioEl) {
      setActiveIndex(-1);
      return;
    }
    const tick = () => {
      const t = audioEl.currentTime;
      // Lines are start-time-sorted, so the latest line with startSec ≤ t is the active one. Linear scan is
      // fine — call transcripts are ~dozens of utterances; switching to binary search would be premature.
      let idx = -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].startSec <= t) idx = i;
        else break;
      }
      setActiveIndex((prev) => (prev === idx ? prev : idx));
    };
    audioEl.addEventListener("timeupdate", tick);
    audioEl.addEventListener("seeked", tick);
    audioEl.addEventListener("play", tick);
    audioEl.addEventListener("loadedmetadata", tick);
    tick(); // seed once in case the audio is already past 0
    return () => {
      audioEl.removeEventListener("timeupdate", tick);
      audioEl.removeEventListener("seeked", tick);
      audioEl.removeEventListener("play", tick);
      audioEl.removeEventListener("loadedmetadata", tick);
    };
  }, [audioEl, lines]);

  return activeIndex;
};

const TranscriptList = ({
  lines,
  activeIndex,
  onSeek,
}: {
  lines: MergedLine[];
  activeIndex: number;
  onSeek: (sec: number) => void;
}) => {
  // Refs to each row, used to scroll the active row into view as audio advances. Slot is sized to match
  // the line count so old refs don't linger if the transcript is replaced (re-renders on detail refetch).
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);
  rowRefs.current.length = lines.length;

  useEffect(() => {
    if (activeIndex < 0) return;
    const el = rowRefs.current[activeIndex];
    if (!el) return;
    // `block: "nearest"` only scrolls if the row is out of view, so the container doesn't fight the user
    // when they manually scroll back to read.
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIndex]);

  return (
    <ol className="space-y-2">
      {lines.map((line, i) => {
        const isActive = i === activeIndex;
        const speakerTint =
          line.speaker === "agent"
            ? isActive
              ? "bg-blue-200/70"
              : "bg-blue-50/40 hover:bg-blue-100/60"
            : isActive
              ? "bg-amber-200/70"
              : "bg-amber-50/40 hover:bg-amber-100/60";
        return (
          <li
            key={i}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
          >
            <button
              type="button"
              onClick={() => onSeek(line.startSec)}
              title={`Přehrát od ${formatMmss(line.startSec)}`}
              // `items-start` anchors the timestamp + speaker label to the top of the row so they line up
              // with the first line of text when the utterance wraps. `text-left` overrides the button's
              // user-agent centering. Active line gets a stronger tint + a navy left-bar so the eye picks
              // up the playback position even when scrolled.
              className={
                "flex w-full items-start gap-3 rounded border-l-4 p-2 text-left text-sm transition-colors " +
                "focus:outline-none focus:ring-2 focus:ring-[#1a2b6b]/30 " +
                (isActive ? "border-[#1a2b6b] " : "border-transparent ") +
                speakerTint
              }
            >
              <span className="shrink-0 font-mono text-xs leading-5 text-[#1a2b6b]">
                {formatMmss(line.startSec)}
              </span>
              <span className="w-20 shrink-0 text-xs font-semibold uppercase leading-5 tracking-wider text-gray-500">
                {line.speaker === "agent" ? "Hlas Emmy" : "Volající"}
              </span>
              <span className="flex-1 leading-5 text-gray-800">{line.text}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

const ProblemPanel = ({ callId, problem }: { callId: string; problem: CallProblem }) => {
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: () => api.resolveCallProblem(callId, problem.execution),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["call", callId] });
      void qc.invalidateQueries({ queryKey: ["calls"] });
      void qc.invalidateQueries({ queryKey: ["call-problems-count"] });
    },
  });

  if (problem.resolvedAt) {
    return (
      <p className="rounded border border-gray-200 bg-gray-50 px-6 py-3 text-sm text-gray-600">
        {PROBLEM_TITLE[problem.kind]} — vyřešil(a) {problem.resolvedBy ?? "—"}, {formatDate(problem.resolvedAt)}
      </p>
    );
  }

  const reason = problem.errorKind
    ? [ERROR_KIND_LABEL[problem.errorKind] ?? problem.errorKind, problem.errorCode && `(${problem.errorCode})`]
        .filter(Boolean)
        .join(" ")
    : null;

  return (
    <section className={`rounded border p-6 ${PROBLEM_BOX_STYLE[problem.kind]}`}>
      <h3 className="mb-2 text-sm font-semibold">{PROBLEM_TITLE[problem.kind]}</h3>
      <p className="text-sm">{PROBLEM_TEXT[problem.kind]}</p>
      {reason && <p className="mt-2 text-sm">Důvod: {reason}</p>}
      {resolve.isError && (
        <p className="mt-2 text-sm">Nepodařilo se uložit: {(resolve.error as Error).message}</p>
      )}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => resolve.mutate()}
          disabled={resolve.isPending}
          className="rounded border border-current bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {resolve.isPending ? "Ukládám…" : "Označit jako vyřešené"}
        </button>
      </div>
    </section>
  );
};

const TranscriptDownload = ({ callId }: { callId: string }) => {
  const mutation = useMutation({
    mutationFn: () => api.getCallTranscriptUrl(callId),
    onSuccess: (r) => {
      window.open(r.url, "_blank", "noopener");
    },
  });
  return (
    <button
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="mt-3 text-xs text-[#1a2b6b] hover:underline disabled:text-gray-400"
    >
      {mutation.isPending ? "Načítám…" : "Stáhnout surový přepis (JSON)"}
    </button>
  );
};

const TokenUsageTable = ({ rows }: { rows: TokenUsageRow[] }) => {
  if (rows.length === 0) return <p className="text-sm text-gray-500">Žádná spotřeba zaznamenána.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="py-2 font-medium">Poskytovatel</th>
          <th className="py-2 font-medium">Model</th>
          <th className="py-2 text-right font-medium">Vstupní tokeny</th>
          <th className="py-2 text-right font-medium">Výstupní tokeny</th>
          <th className="py-2 text-right font-medium">Audio s</th>
          <th className="py-2 text-right font-medium">Cena (¢)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-gray-100">
            <td className="py-2">{r.provider}</td>
            <td className="py-2 font-mono text-xs">{r.model}</td>
            <td className="py-2 text-right">{r.inputTokens ?? "—"}</td>
            <td className="py-2 text-right">{r.outputTokens ?? "—"}</td>
            <td className="py-2 text-right">{r.audioSeconds ?? "—"}</td>
            <td className="py-2 text-right">{r.costEurCents ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const EventsList = ({ events }: { events: CallEventRow[] }) => {
  if (events.length === 0) return <p className="text-sm text-gray-500">Žádné události.</p>;
  return (
    <ol className="space-y-2">
      {events.map((e, i) => (
        <li key={i} className="flex gap-4 border-t border-gray-100 pt-2 text-sm first:border-t-0 first:pt-0">
          <span className="w-36 shrink-0 font-mono text-xs text-gray-500">
            {formatDate(e.occurredAt)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="font-medium">{e.eventType}</span>
            {e.payload !== undefined && e.payload !== null && (
              <pre className="mt-1 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-2 text-xs text-gray-700">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
};

const STATUS_STYLE: Record<CallStatus, string> = {
  in_progress:              "bg-blue-50 text-blue-700",
  awaiting_post_processing: "bg-amber-50 text-amber-700",
  done:                     "bg-green-50 text-green-700",
  failed:                   "bg-red-50 text-red-700",
};

const STATUS_LABEL: Record<CallStatus, string> = {
  in_progress:              "Probíhá",
  awaiting_post_processing: "Zpracování",
  done:                     "Dokončeno",
  failed:                   "Selhalo",
};

const IDENT_LABEL: Record<IdentificationPath, string> = {
  caller_id_match:      "Rozpoznáno dle čísla",
  fallback_extraction:  "Extrahováno LLM",
  unknown:              "Neznámé",
};

const StatusBadge = ({ status }: { status: CallStatus }) => (
  <span
    className={`inline-flex items-center rounded-full px-3 py-1 text-sm ${STATUS_STYLE[status] ?? "bg-gray-100 text-gray-700"}`}
  >
    {STATUS_LABEL[status] ?? status}
  </span>
);

const ClosureBadge = ({ kind }: { kind: ClosureKind }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">
    {CLOSURE_LABEL[kind]}
  </span>
);

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatMmss = (sec: number): string => {
  const total = Math.max(0, Math.floor(sec));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

